import { AbilityBuilder, Ability } from '@casl/ability';
import {
  ACTION_TYPE,
  ADS_TRACER_ZIPKIN_LINK,
  AD_INVESTIGATOR,
  ALLOW,
  ASSET_REVIEWER_LINK,
  DENY,
  EDIT,
  EDIT_ALL,
  FULL_ACCESS_OVERRIDES,
  NAME,
  PERMISSION_TYPE,
  PERMISSIONS,
  TOOL_NAME_MAP,
  TOOL_PERMISSION_MAP,
  TOOLS_LIST,
  VAST_TRACKER_TABS,
  VIEW,
  VIEW_ZIPKIN_LINK,
  VIEW_ASSET_REVIEWER_LINK,
} from '../constants';
import {
  ACCESS_AD_SERVER,
  ACCESS_CONTENT_PARTNER,
  ACCESS_CONTENT_PARTNER_GROUP,
  ACCESS_DSP,
  ACCESS_SOURCE,
  ACCESS_APP,
  AD_SERVER,
  AD_UNIT_ID,
  ALL,
  ALL_AD_SERVERS,
  ALL_AD_UNIT_IDS,
  ALL_CONTENT_PARTNERS,
  ALL_DSPS,
  ALL_SOURCES,
  ALL_APPS,
  CONTENT_PARTNER,
  CT_FILTER_FIELD,
  CT_TABLE_COLUMN,
  DSP,
  DSP_IDS,
  SOURCE,
  SOURCES,
  TOOL,
  TOOL_PERMISSIONS_CAPS,
  ALLOWED_APPS,
  APP,
} from './constants';
import {
  TRACE_DISNEY_PLUS_ACCOUNT,
  TRACE_ESPN_ACCOUNT,
  TRACE_FUBO_ACCOUNT,
  TRACE_HULU_ACCOUNT,
  TRACE_HULU_PLUS_ACCOUNT,
  TRACE_NEWS_ENT_ACCOUNT,
  TRACE_OFF_PLATFORM_ACCOUNT,
  TRACE_ROKU_ACCOUNT,
} from '../pages/AdsTracer/constants';

export const defineRules = (userClaim) => {
  const abilities = new Ability();
  const { can, cannot, rules } = new AbilityBuilder(Ability);

  // Vast Tracker
  const allowedVastTrackerTabPermissions = userClaim[PERMISSIONS].filter(
    (permission) => permission[PERMISSION_TYPE] === VAST_TRACKER_TABS && permission[ACTION_TYPE] === ALLOW
  );
  if (allowedVastTrackerTabPermissions) {
    allowedVastTrackerTabPermissions.forEach((tab) => can(VIEW, tab.name));
  }

  // Ad Investigator
  const allowedFullAccessAdInvestigator = userClaim[PERMISSIONS].find(
    (permission) =>
      permission['name'] === FULL_ACCESS_OVERRIDES &&
      permission[PERMISSION_TYPE] === TOOL_PERMISSIONS_CAPS &&
      permission[ACTION_TYPE] === ALLOW
  );
  if (allowedFullAccessAdInvestigator) {
    can(EDIT_ALL, AD_INVESTIGATOR);
  }

  // Ad Unit Ids
  const allowedAllAdUnitIds = userClaim[PERMISSIONS].find(
    (permission) =>
      permission['name'] === ALL_AD_UNIT_IDS &&
      permission[PERMISSION_TYPE] === AD_UNIT_ID &&
      permission[ACTION_TYPE] === ALLOW
  );
  if (allowedAllAdUnitIds) {
    can(VIEW, ALL_AD_UNIT_IDS);
  }

  // Content Partners
  const allowedAllContentPartners = userClaim[PERMISSIONS].find(
    (permission) =>
      permission['name'] === ALL_CONTENT_PARTNERS &&
      permission[PERMISSION_TYPE] === CONTENT_PARTNER &&
      permission[ACTION_TYPE] === ALLOW
  );
  const allowedContentPartnerGroups = userClaim['content-partner-groups'].map((group) => group.name);
  const allowedContentPartners = userClaim['content-partners'];
  if (allowedAllContentPartners) {
    can(ACCESS_CONTENT_PARTNER_GROUP, ALL);
    can(ACCESS_CONTENT_PARTNER, ALL);
  } else {
    allowedContentPartnerGroups.forEach((group) => can(ACCESS_CONTENT_PARTNER_GROUP, group));
    allowedContentPartners.forEach((partner) => can(ACCESS_CONTENT_PARTNER, partner.name.toLowerCase()));
  }

  // Ad Servers
  const allowedAllAdServers =
    userClaim[PERMISSIONS].find(
      (permission) =>
        permission['name'] === ALL_AD_SERVERS &&
        permission[PERMISSION_TYPE] === AD_SERVER &&
        permission[ACTION_TYPE] === ALLOW
    ) || [];
  const allowedAdServers =
    userClaim[PERMISSIONS].filter(
      (permission) =>
        permission['name'] !== ALL_AD_SERVERS &&
        permission[PERMISSION_TYPE] === AD_SERVER &&
        permission[ACTION_TYPE] === ALLOW
    ) || [];
  if (allowedAllAdServers.length !== 0) {
    can(ACCESS_AD_SERVER, ALL);
  } else {
    allowedAdServers.forEach((adserver) => can(ACCESS_AD_SERVER, adserver.name.toLowerCase()));
  }

  // DSPs
  const allowedAllDSPS = userClaim[PERMISSIONS].find(
    (permission) =>
      permission['name'] === ALL_DSPS && permission[PERMISSION_TYPE] === DSP && permission[ACTION_TYPE] === ALLOW
  );
  const allowedDSPIds = userClaim[DSP_IDS] || [];
  if (allowedAllDSPS) {
    can(ACCESS_DSP, ALL);
  } else {
    allowedDSPIds.forEach((id) => can(ACCESS_DSP, id));
  }

  // Sources
  const allowedAllSources = userClaim[PERMISSIONS].find(
    (permission) =>
      permission['name'] === ALL_SOURCES && permission[PERMISSION_TYPE] === SOURCE && permission[ACTION_TYPE] === ALLOW
  );

  const allowedSources = userClaim[SOURCES] || [];
  if (allowedAllSources) {
    can(ACCESS_SOURCE, ALL);
  } else {
    allowedSources.forEach((source) => can(ACCESS_SOURCE, source));
  }

  // Apps
  const allowedAllApps = userClaim[PERMISSIONS].find(
    (permission) =>
      permission['name'] === ALL_APPS && permission[PERMISSION_TYPE] === APP && permission[ACTION_TYPE] === ALLOW
  );

  const allowedApps = userClaim[ALLOWED_APPS] || [];
  if (allowedAllApps || allowedApps.length === 0) {
    can(ACCESS_APP, ALL);
  } else {
    allowedApps.forEach((app) => can(ACCESS_APP, app.toUpperCase().replace(/ /g, '_')));
  }

  // Creative Tracker
  const canViewAssetReviewerLink = userClaim[PERMISSIONS].some(
    (p) =>
      p[PERMISSION_TYPE] === TOOL_PERMISSIONS_CAPS && p[ACTION_TYPE] === ALLOW && p[NAME] === VIEW_ASSET_REVIEWER_LINK
  );
  if (canViewAssetReviewerLink) {
    can(VIEW, ASSET_REVIEWER_LINK);
  }

  const canViewAdsTracerZipkinLink = userClaim[PERMISSIONS].some(
    (p) => p[PERMISSION_TYPE] === TOOL_PERMISSIONS_CAPS && p[ACTION_TYPE] === ALLOW && p[NAME] === VIEW_ZIPKIN_LINK
  );
  if (canViewAdsTracerZipkinLink) {
    can(VIEW, ADS_TRACER_ZIPKIN_LINK);
  }

  const allowedCreativeTrackerCols = userClaim[PERMISSIONS].filter(
    (permission) => permission[PERMISSION_TYPE] === CT_TABLE_COLUMN && permission[ACTION_TYPE] === ALLOW
  );
  allowedCreativeTrackerCols.forEach((col) => can(VIEW, col.name));

  const allowedCreativeTrackerFilters = userClaim[PERMISSIONS].filter(
    (permission) => permission[PERMISSION_TYPE] === CT_FILTER_FIELD && permission[ACTION_TYPE] === ALLOW
  );
  allowedCreativeTrackerFilters.forEach((filterField) => can(VIEW, filterField.name));

  // All Tools
  const tools = userClaim[PERMISSIONS].filter((permission) => permission[PERMISSION_TYPE] === TOOL);
  const toolPermissions = userClaim[PERMISSIONS].filter(
    (permission) => permission[PERMISSION_TYPE] === TOOL_PERMISSIONS_CAPS
  );

  // Default deny for Add Account tiles
  const ACCOUNT_LINK_PERMISSIONS = [
    TRACE_DISNEY_PLUS_ACCOUNT,
    TRACE_HULU_ACCOUNT,
    TRACE_HULU_PLUS_ACCOUNT,
    TRACE_ESPN_ACCOUNT,
    TRACE_OFF_PLATFORM_ACCOUNT,
    TRACE_NEWS_ENT_ACCOUNT,
    TRACE_FUBO_ACCOUNT,
    TRACE_ROKU_ACCOUNT,
  ];
  ACCOUNT_LINK_PERMISSIONS.forEach((perm) => cannot(VIEW, perm));

  // Allow/deny Add Account tiles based on TOOL_PERMISSIONS
  [...toolPermissions]
    .filter((permission) => ACCOUNT_LINK_PERMISSIONS.includes(permission.name))
    .forEach((permission) => {
      if (permission[ACTION_TYPE] === DENY) {
        cannot(VIEW, permission.name);
      } else if (permission[ACTION_TYPE] === ALLOW) {
        can(VIEW, permission.name);
      }
    });

  // default deny access
  Object.entries(TOOLS_LIST).forEach((key, value) => {
    cannot(EDIT, key);
    cannot(VIEW, key);
  });

  // edit permissions
  tools.forEach((tool) => {
    const toolName = TOOL_NAME_MAP[tool.name];
    if (toolName && tool[ACTION_TYPE] === ALLOW) {
      can(EDIT, toolName);
      can(VIEW, toolName);
    }
  });

  // view permissions and special case edit permissions
  toolPermissions.forEach((permission) => {
    let tool = TOOL_PERMISSION_MAP[permission.name];
    let type = VIEW;
    if (tool && TOOLS_LIST[tool]?.permissions) {
      // special case edit permission
      if (TOOLS_LIST[tool].permissions.edit?.includes(permission.name)) {
        type = EDIT;
        tool = permission.name;
      }
    }
    if (tool && permission[ACTION_TYPE] === DENY) {
      cannot(type, tool);
    } else if (tool && permission[ACTION_TYPE] === ALLOW) {
      can(type, tool);
    }
  });

  abilities.update(rules);
  return abilities;
};

// TODO: remove
export const isUserContentPartner = (userClaim) => {
  return userClaim?.roles?.some((role) => role.name === 'MP_BASE_ROLE');
};
